import { Injectable } from "@angular/core";
import { IContentControlManager } from '../../domain/content-control/manager';
import {
  bufferTime,
  catchError,
  concatMap,
  debounceTime,
  from,
  mergeMap,
  Observable,
  of,
  Subject,
  Subscription,
  throwError,
  timeout,
  toArray,
} from 'rxjs';
import { ContentControlItem, ContentControlItemLinkedFile } from '../../domain/content-control/item';
import { ContentControlledItemsSpecifications } from '../../domain/content-control/specifications';
import { IContentControlRepository } from '../../domain/content-control/repository';
import {
  ContentControlledEditingDirective,
  ContentControlledEditingDirectiveScreenshot,
  ContentControlledEditingDirectiveViewModel,
} from '../../domain/content-control/editing-directive';
import { ContentControlledReportableEvent } from '../../domain/content-control/reportable-event';
import { ContentControlledKeyframe } from '../../domain/content-control/keyframe';
import ContentControlStudioOperation from '../../domain/content-control/operations/content-control-studio-operation';
import ContentControlStudioAddEventOperation from '../../domain/content-control/operations/content-control-studio-add-event-operation';
import ContentControlStudioAddKeyframeToEventOperation from '../../domain/content-control/operations/content-control-studio-add-keyframe-to-event-operation';
import ContentControlStudioUpdateKeyframeToEventOperation from '../../domain/content-control/operations/content-control-studio-update-keyframe-to-event-operation';
import ContentControlStudioRemoveKeyframeToEventOperation from '../../domain/content-control/operations/content-control-studio-remove-keyframe-to-event-operation';
import ContentControlStudioRemoveEventOperation from '../../domain/content-control/operations/content-control-studio-remove-event-operation';
import ContentControlStudioRevertEventOperation from '../../domain/content-control/operations/content-control-studio-revert-event-operation';
import ContentControlStudioUpdateEventFromSegmentOperation from '../../domain/content-control/operations/content-control-studio-update-event-from-segment-operation';
import { SubtitleSegment } from '../../domain/subtitle-segment';
import {
  ContentControlledVODSettings,
  IContentControlledItemSettings,
} from '../../domain/content-control/settings';
import { ICommonUtilInterface } from '../../domain/common/common-util.interface';
import {
  ContentControlledEventActionEnum,
  ContentControlledLinkedFileMediaTypeEnum,
  ContentControlledMediaTypeEnum,
} from '../../domain/content-control/enums';
import ContentControlStudioCompositeOperation from '../../domain/content-control/operations/content-control-studio-composite-operation';
import { FormatNumber } from "../../domain/common/ui-utils";
import { TenantSettingsRepository } from "../../domain/tenant-settings-repository";
import { TenantService } from "../../services/tenant.service";
import { CachedTenantSettingsRepositoryImpl } from "../repositories/tenant/tenantSettings";
import { IMediaTranscoderRepository } from "../../domain/media-transcoder/media-transcoder.repository";
import { TranscodingDestination } from "../../domain/media-transcoder/transcoding-destination";
import { MediaRepository } from "../../domain/media-repository";
import { MediaType } from "../../domain/media";
import {
  ETranscodingJobStatus,
  TranscoderJobDetails,
} from "../../domain/media-transcoder/transcoder-job";
import { EMediaTranscodingItemStatus } from "../../domain/media-transcoder/transcoding-item";
import { TenantSettings } from "../../domain/tenant-settings";
import { ITimestampToTimelineConverter } from '../../domain/content-control/provider';
import { HlsStreamVideoSource } from '../../domain/content-control/hls-stream-video-source';

@Injectable()
export class ContentControlManagerImpl implements IContentControlManager {
  private _item: ContentControlItem;
  private _isLive: boolean = false;
  private _selectedSegment: ContentControlledEditingDirectiveViewModel;
  private _audioCtx: AudioContext;
  private _audioOscillator: OscillatorNode;
  private _audioGain: GainNode;
  private _undoStack: ContentControlStudioOperation[] = [];
  private _redoStack = [];
  private _subtitles: { [lang: string]: Array<SubtitleSegment> } = {};
  private _eventsUpdatedDebounceTimeout: any;
  private _debounceOperationSubject: Subject<ContentControlStudioOperation>;
  private _liveSaveSubject: Subject<void>;
  private _videoFps: number;
  private _vidoeFpsIsDF: boolean;
  private _mediaStartTimeStampMs;
  private _tenantTranscodingDestinations: TranscodingDestination[];
  private _tenantSettings: TenantSettings;
  private _displayContentControlStudioTimeInFrames: boolean = false;
  screenshotQueue: Subject<ContentControlledEditingDirectiveScreenshot>;
  screenshotTaken: Subject<ContentControlledEditingDirectiveScreenshot>;
  itemUpdated: Subject<boolean>;
  itemSaved: Subject<boolean>;
  eventsUpdated: Subject<void>;
  selectedSegmentRemoved: Subject<number>;
  private _screenshotHLSProvider: HlsStreamVideoSource;
  private _liveHlsProviderForScreenshotConversion: ITimestampToTimelineConverter | null = null;
  private _screenshotVideoElement: HTMLVideoElement;
  private _screenshotQueueSubscription: Subscription;

  constructor(
    private _repository: IContentControlRepository,
    private _commonUtils: ICommonUtilInterface,
    private tenantSettingsRepository: TenantSettingsRepository,
    private _mediaTranscoderRepository: IMediaTranscoderRepository,
    private _mediaRepository: MediaRepository,
    private _contentControlRepository: IContentControlRepository,
  ) {
    this.itemUpdated = new Subject<boolean>();
    this.itemSaved = new Subject<boolean>();
    this.screenshotTaken =
      new Subject<ContentControlledEditingDirectiveScreenshot>();
    this.screenshotQueue =
      new Subject<ContentControlledEditingDirectiveScreenshot>();
    this.eventsUpdated = new Subject<void>();
    this.selectedSegmentRemoved = new Subject<number>();
    this._screenshotQueueSubscription = this.screenshotQueue
      .pipe(
        bufferTime(3000, undefined, 20), // Buffer tasks either every 100ms or when 2 tasks are collected
        concatMap((batch) =>
          from(batch).pipe(
            mergeMap(this.takeScreenshot.bind(this), 20), // Process up to 2 tasks concurrently within each batch
            toArray(),
          ),
        ),
      )
      .subscribe({
        error: (err) => console.error("Processed screenshots Error:", err),
      });
    this._debounceOperationSubject = new Subject();
    this._debounceOperationSubject
      .pipe(debounceTime(20)) // lower than fps
      .subscribe((operation) => {
        this.executeOperation(operation);
      });
    this._liveSaveSubject = new Subject<void>();
    this._liveSaveSubject
      .pipe(debounceTime(500))
      .subscribe(async () => {
        try {
          const result = await this._repository.saveLive(this._item);
          for (const op of this._undoStack) op.isSaved = true;
          this.itemSaved.next(result);
        } catch (e) {
          console.error('live auto-save failed', e);
          this.itemSaved.next(false);
        }
      });
    this._mediaTranscoderRepository
      .getTenantTranscodingDestinations()
      .then((td) => (this._tenantTranscodingDestinations = td));
  }
  async getItemByChannelUid(channelUid: string): Promise<boolean> {
    const item = await this._repository.getItemByChannelUid(channelUid);
    if (item) {
      this._isLive = true;
      await this.postItemLoadActions(item);
      return true;
    }
    return false;
  }
  // async getItemByMediaId(mediaId: number): Promise<boolean> {
  //   const item = await this._ensureItemExists(mediaId);
  //   if (item) {
  //     await this.postItemLoadActions(item);
  //     return true;
  //   } else {
  //     return false;
  //   }
  // }

  get isLive(): boolean {
    return this._isLive;
  }

  get displayContentControlStudioTimeInFrames(): boolean {
    return this._displayContentControlStudioTimeInFrames;
  }

  upsertCutEditingDirectiveEvent(
    cutEvent: ContentControlledEditingDirective,
    eventsToChange: Array<ContentControlledEditingDirective>,
    selectedSubtitles: Array<string>,
    segment?: ContentControlledEditingDirectiveViewModel,
  ): void {
    let cutOp: ContentControlStudioOperation;
    let startsAtMs = cutEvent.startsAtMs;
    let endsAtMs = cutEvent.endsAtMs;
    if (
      this._item.editingDirectives.findIndex((x) => x.id == cutEvent.id) == -1
    ) {
      // insert
      cutOp = new ContentControlStudioAddEventOperation(
        cutEvent,
        this._item.editingDirectives,
        () => {
          if (
            this._item.editingDirectives.findIndex((x) => x.id == cutEvent.id) >
            -1
          ) {
            this._selectedSegment = (
              cutEvent as ContentControlledEditingDirective
            ).toView(0);
          } else {
            this._selectedSegment = this.createNewSegment();
            this._selectedSegment.startsAtMs = startsAtMs;
            this._selectedSegment.endsAtMs = endsAtMs;
          }
          this.sendEventsUpdated();
        },
      );
    } else if (segment) {
      // update
      cutEvent = this._item.editingDirectives.find((x) => x.id == segment.id);
      startsAtMs = segment.startsAtMs;
      endsAtMs = segment.endsAtMs;
      cutOp = new ContentControlStudioUpdateEventFromSegmentOperation(
        cutEvent,
        segment,
        cutEvent.title,
        cutEvent.action,
        cutEvent.isApproved,
        cutEvent.isMitigated,
        cutEvent.startsAtMs,
        cutEvent.endsAtMs,
        [...cutEvent.keyframes],
        cutEvent.startFrame,
        cutEvent.endFrame,
        undefined,
        undefined,
        undefined,
        undefined,
        () => {
          this.sendEventsUpdated();
        },
      );
    } else {
      return;
    }

    const compositeOperation = new ContentControlStudioCompositeOperation(
      () => {},
      () => {},
      () => {
        this.sendEventsUpdated();
      },
    );
    compositeOperation.addOperation(cutOp);

    const fixSubtitles = (
      segment: ContentControlledEditingDirectiveViewModel,
    ) => {
      selectedSubtitles.map((x) => {
        segment.subtitles.mergeSubtitleSegments(
          x,
          this.getSubtitleSegments(x, segment.startsAtMs, segment.endsAtMs),
        );
      });
    };
    const fixKeyframes = (
      segment: ContentControlledEditingDirectiveViewModel,
      fromStart = true,
    ) => {
      if (
        !segment.keyframes ||
        Array.isArray(segment.keyframes) == false ||
        segment.keyframes.length == 0
      ) {
        return;
      }

      const newKeyframes = [
        ...segment.keyframes.filter(
          (x) =>
            x.timeInMs <= segment.endsAtMs &&
            (fromStart || x.timeInMs >= segment.startsAtMs),
        ),
      ];
      if (
        newKeyframes.length == 0 ||
        newKeyframes.length == segment.keyframes.length
      ) {
        return;
      }
      let leftovers: ContentControlledKeyframe[] = [];
      if (fromStart) {
        leftovers = [
          ...segment.keyframes.filter((x) => x.timeInMs > segment.endsAtMs),
        ];
        if (
          leftovers.length > 0 &&
          newKeyframes[newKeyframes.length - 1].timeInMs != segment.endsAtMs
        ) {
          const kf = leftovers[0];

          const lkf = newKeyframes[newKeyframes.length - 1];
          kf.ingest(
            lkf.interpolatePlacementOnCanvas(
              kf,
              (segment.endsAtMs - lkf.timeInMs) / (kf.timeInMs - lkf.timeInMs),
            ),
          );
          kf.timeInMs = segment.endsAtMs;
          newKeyframes.push(kf);
        }
      } else {
        leftovers = [
          ...segment.keyframes.filter((x) => x.timeInMs < segment.startsAtMs),
        ];
        if (
          leftovers.length > 0 &&
          newKeyframes[0].timeInMs != segment.startsAtMs
        ) {
          let kf = leftovers[leftovers.length - 1];
          let nkf = newKeyframes[0];
          kf.ingest(
            kf.interpolatePlacementOnCanvas(
              nkf,
              (segment.startsAtMs - kf.timeInMs) / (nkf.timeInMs - kf.timeInMs),
            ),
          );
          kf.timeInMs = segment.startsAtMs;
          newKeyframes.unshift(kf);
        }
      }
      segment.keyframes = newKeyframes;
    };

    let highestId = Math.max(
      cutEvent.id,
      Math.max(
        ...this.editingDirectives.map((x) => x.id).filter((x) => x < 100000),
      ),
    );
    for (const event of eventsToChange) {
      if (event.startsAtMs >= startsAtMs && event.endsAtMs <= endsAtMs) {
        // remove event since it's getting cut
        const removeOp = new ContentControlStudioRemoveEventOperation(
          event,
          this._item.editingDirectives,
          () => {},
        );
        //compositeOperation.addOperation(removeOp);
      } else {
        if (
          event.startsAtMs < startsAtMs &&
          event.endsAtMs > startsAtMs &&
          event.endsAtMs < endsAtMs
        ) {
          // change event mark out since it's getting cut
          const segment = event.toView(0);
          segment.endsAtMs = startsAtMs - 1 / this._item.videoFps;
          if (
            segment.action === ContentControlledEventActionEnum.EditSubtitles
          ) {
            fixSubtitles(segment);
          }
          fixKeyframes(segment, true);
          const updateOp =
            new ContentControlStudioUpdateEventFromSegmentOperation(
              event,
              segment,
              event.title,
              event.action,
              event.isApproved,
              event.isMitigated,
              event.startsAtMs,
              event.endsAtMs,
              [...event.keyframes],
              event.startFrame,
              "",
              null,
              { ...event.instructions },
              event.subtitles?.clone() || undefined,
              event.createdAt,
              () => {},
            );
          compositeOperation.addOperation(updateOp);
        } else if (
          event.startsAtMs < endsAtMs &&
          event.startsAtMs > startsAtMs &&
          event.endsAtMs > endsAtMs
        ) {
          // change event mark in since it's getting cut
          const segment = event.toView(0);
          segment.startsAtMs = cutEvent.endsAtMs + 1 / this._item.videoFps;
          if (
            segment.action === ContentControlledEventActionEnum.EditSubtitles
          ) {
            fixSubtitles(segment);
          }
          fixKeyframes(segment, false);
          const updateOp =
            new ContentControlStudioUpdateEventFromSegmentOperation(
              event,
              segment,
              event.title,
              event.action,
              event.isApproved,
              event.isMitigated,
              event.startsAtMs,
              event.endsAtMs,
              [...event.keyframes],
              "",
              event.endFrame,
              null,
              { ...event.instructions },
              event.subtitles?.clone() || undefined,
              event.createdAt,
              () => {},
            );
          compositeOperation.addOperation(updateOp);
        } else if (startsAtMs > event.startsAtMs && endsAtMs < event.endsAtMs) {
          // event is longer than cut event, so we need to split it
          // let start with the first
          const oneFrameInMS = (1 / this._item.videoFps) * 1000;
          const segment = event.toView(0);
          segment.endsAtMs = startsAtMs - oneFrameInMS;
          if (
            segment.action === ContentControlledEventActionEnum.EditSubtitles
          ) {
            fixSubtitles(segment);
          }
          fixKeyframes(segment, true);
          const updateOp =
            new ContentControlStudioUpdateEventFromSegmentOperation(
              event,
              segment,
              event.title,
              event.action,
              event.isApproved,
              event.isMitigated,
              event.startsAtMs,
              event.endsAtMs,
              [...event.keyframes],
              event.startFrame,
              event.endFrame,
              null,
              { ...event.instructions },
              event.subtitles?.clone() || undefined,
              event.createdAt,
              () => {},
            );
          compositeOperation.addOperation(updateOp);
          // now lets do the second part

          const event2 = new ContentControlledEditingDirective(
            ++highestId,
            event.mediaType,
            cutEvent.endsAtMs + oneFrameInMS,
            event.endsAtMs,
            event.title,
            event.isApproved,
            event.isMitigated,
            event.action,
            [...event.keyframes],
            "",
            event.endFrame,
            { ...event.instructions },
            event.subtitles?.clone() || undefined,
            undefined,
            undefined,
          );
          let segment2 = event2.toView(0);
          if (
            segment2.action === ContentControlledEventActionEnum.EditSubtitles
          ) {
            fixSubtitles(segment2);
            event2.subtitles = segment2.subtitles;
          }
          fixKeyframes(segment2, false);
          event2.keyframes = [...segment2.keyframes];
          const addOp = new ContentControlStudioAddEventOperation(
            event2,
            this._item.editingDirectives,
            () => {},
          );
          compositeOperation.addOperation(addOp);
        }
      }
    }
    this.executeOperation(compositeOperation);
  }
  get itemSettings(): IContentControlledItemSettings {
    return this._item.itemSettings as IContentControlledItemSettings;
  }
  set updateSettings(settings: IContentControlledItemSettings) {
    this._item.itemSettings = settings;
  }

  async loadSubtitle(
    lang: string,
    url: string,
    fps: number = 25,
  ): Promise<void> {
    if (this._subtitles[lang]) {
      return;
    }
    const subtitle = await this._repository.loadSubtitle(url);
    if (subtitle && subtitle["Language"] && subtitle["Language"] == lang) {
      if (subtitle["Subs"] && Array.isArray(subtitle["Subs"])) {
        this._subtitles[lang] = subtitle["Subs"].map(
          (x) =>
            new SubtitleSegment(
              x["Text"],
              x["StartTimeMS"],
              x["EndTimeMS"],
              x["Commands"],
            ),
        );
      } else {
        this._subtitles[lang] = [];
      }
    }
  }

  getSubtitleSegments(
    lang: string,
    startsAtMs: number,
    endsAtMs: any,
  ): Array<SubtitleSegment> {
    if (!lang) return;
    if (!this._subtitles[lang]) return;

    startsAtMs = this.syncSubtitlesWithVideo(startsAtMs / 1000, lang) * 1000;
    endsAtMs = this.syncSubtitlesWithVideo(endsAtMs / 1000, lang) * 1000;
    return this._subtitles[lang].filter(
      (x) =>
        (x.StartTimeMS >= startsAtMs && x.EndTimeMS <= endsAtMs) ||
        (startsAtMs > x.StartTimeMS && startsAtMs < x.EndTimeMS),
    );
  }
  getCurrentSubtitleSegments(
    lang: string,
    currentTimeInMs: number,
  ): Array<SubtitleSegment> {
    if (!lang) return;
    if (!this._subtitles[lang]) return;
    currentTimeInMs =
      this.syncSubtitlesWithVideo(currentTimeInMs / 1000, lang) * 1000;
    return this._subtitles[lang].filter(
      (x) => currentTimeInMs >= x.StartTimeMS && currentTimeInMs <= x.EndTimeMS,
    );
  }
  adjustTimeToSubtitle(timeInMs: number, lang: string): number {
    return this.syncSubtitlesWithVideo(timeInMs / 1000, lang) * 1000;
  }
  private getSubtitleFps(lang: string): number {
    const subtitle = this._item.linkedFiles.find(
      (x) =>
        x.fileType == ContentControlledLinkedFileMediaTypeEnum.Subtitle &&
        x.language == lang,
    );
    return subtitle && subtitle.fps ? subtitle.fps : 0;
  }
  private syncSubtitlesWithVideo(
    currentTime: number,
    subtitleLang: string,
  ): number {
    let subtitleFile: ContentControlItemLinkedFile;
    if (subtitleLang) {
      subtitleFile = this._item.SubtitleFiles.find(
        (x) => x.language == subtitleLang,
      );
      if (subtitleFile && subtitleFile.playbackSpeed) {
        currentTime = currentTime * subtitleFile.playbackSpeed;
      } else {
        const subtitleFps = this.getSubtitleFps(subtitleLang);
        if (subtitleFps !== this._item.videoFps) {
          if (subtitleFps > this._item.videoFps) {
            currentTime = currentTime * (this._item.videoFps / subtitleFps);
          } else {
            currentTime = currentTime / (subtitleFps / this._item.videoFps);
          }
        }
      }
    }
    return (
      currentTime + this.mediaStartTimeStampMs / 1000 + subtitleFile?.offset ||
      0
    );
  }
  get mediaStartTimeStampMs(): number {
    return this._mediaStartTimeStampMs;
  }
  get isVideoFPSIsDropFrame(): boolean {
    return this._vidoeFpsIsDF;
  }
  get hasChanges(): boolean {
    return this._undoStack.filter((u) => u.isSaved === false).length > 0;
  }

  get selectedSegmentIsEvent(): boolean {
    return (
      this.selectedSegment &&
      this._item.editingDirectives.findIndex(
        (x) => x.id === this.selectedSegment.id,
      ) > -1
    );
  }

  get videoUrl(): string {
    return this._item?.previewVideoUrl;
  }
  get mediaName(): string {
    return this._item?.mediaName || "";
  }

  get selectedSegment(): ContentControlledEditingDirectiveViewModel {
    return this._selectedSegment;
  }
  get selectedEvent(): ContentControlledEditingDirective {
    if (!this._selectedSegment) {
      return null;
    }
    return this.editingDirectives.find((x) => x.id == this._selectedSegment.id);
  }
  get selectedEventKeyframesTime(): number[] {
    return this.selectedEvent
      ? [...this.selectedEvent.keyframes.map((x) => x.timeInMs)]
      : [];
  }

  get videoFps(): number {
    return this._videoFps;
  }
  get editingDirectives(): ContentControlledEditingDirective[] {
    return this._item?.editingDirectives || [];
  }
  get reportableEvents(): ContentControlledReportableEvent[] {
    return this._item?.reportableEvents || [];
  }
  get audioFiles(): ContentControlItemLinkedFile[] {
    return this._item?.AudioFiles;
  }
  get subtitleFiles(): ContentControlItemLinkedFile[] {
    return this._item?.SubtitleFiles;
  }
  set selectedSegment(segment: ContentControlledEditingDirectiveViewModel) {
    this._selectedSegment = segment;
  }
  createNewSegment(): ContentControlledEditingDirectiveViewModel {
    let newId = 1;
    if (this.editingDirectives.length > 0) {
      const higestObj = this.editingDirectives.reduce((highest, obj) => {
        return obj.id < 100000 && (highest == null || obj.id > highest.id)
          ? obj
          : highest;
      }, null);
      if (higestObj) {
        newId = higestObj.id + 1;
      }
    }
    const newSegment = new ContentControlledEditingDirectiveViewModel(
      newId,
      0,
      ContentControlledMediaTypeEnum.None,
      0,
      0,
      "",
      false,
      false,
      "",
      "",
      [],
      ContentControlledEventActionEnum.Empty,
      undefined,
      undefined,
      undefined,
      undefined,
    );
    this.selectedSegment = newSegment;
    return this.selectedSegment;
  }

  updateSelectedSegment(
    tempSegment: ContentControlledEditingDirectiveViewModel,
    doAsyncTasks: boolean = false,
  ) {
    this._selectedSegment.mediaType = tempSegment.mediaType;
    this._selectedSegment.title = tempSegment.title;
    this._selectedSegment.action = tempSegment.action;
    this._selectedSegment.instructions = tempSegment.instructions;
    this._selectedSegment.keyframes = tempSegment.keyframes;
    this._selectedSegment.isMitigated = tempSegment.isMitigated;
    this._selectedSegment.subtitles = tempSegment.subtitles;
    this._selectedSegment.startsAtMs = tempSegment.startsAtMs;
    this._selectedSegment.endsAtMs = tempSegment.endsAtMs;
    this._selectedSegment.mediaId = tempSegment.mediaId;
    if (doAsyncTasks) {
      this.updateSegmentScreenshots(this._selectedSegment);
    }
  }

  updateSelectedSegmentKeyframes(
    keyframes: Array<ContentControlledKeyframe>,
  ): void {
    this._selectedSegment.keyframes = keyframes;
  }
  addEditingDirectiveEvent(event: ContentControlledEditingDirective) {
    if (this._item.editingDirectives.findIndex((x) => x.id == event.id) == -1) {
      const startsAtMs = event.startsAtMs;
      const endsAtsMs = event.endsAtMs;
      const op = new ContentControlStudioAddEventOperation(
        event,
        this._item.editingDirectives,
        () => {
          if (
            this._item.editingDirectives.findIndex((x) => x.id == event.id) ==
            -1
          ) {
            this._selectedSegment = this.createNewSegment();
            this._selectedSegment.startsAtMs = startsAtMs;
            this._selectedSegment.endsAtMs = endsAtsMs;
          }
          this.sendEventsUpdated();
        },
      );
      this.executeOperation(op);
    }
  }

  revertEditingDirectiveEvent(event: ContentControlledEditingDirective) {
    if (this._item.editingDirectives.findIndex((x) => x.id == event.id) > -1) {
      const re = this._item.reportableEvents.find((x) => x.id == event.id);
      if (!re) return;
      const op = new ContentControlStudioRevertEventOperation(
        event,
        re,
        re.title,
        event.title,
        event.action,
        [...event.keyframes],
        () => {
          this.sendEventsUpdated();
        },
      );
      this.executeOperation(op);
    }
  }
  updateEditingDirectiveEventFromSegment(
    segment: ContentControlledEditingDirectiveViewModel,
    keyframe?: ContentControlledKeyframe,
  ) {
    const event = this._item.editingDirectives.find((x) => x.id == segment.id);
    if (event.mediaType != segment.mediaType) {
      this.itemUpdated.next(true);
    }
    const op = new ContentControlStudioUpdateEventFromSegmentOperation(
      event,
      segment,
      event.title,
      event.action,
      event.isApproved,
      event.isMitigated,
      event.startsAtMs,
      event.endsAtMs,
      [...event.keyframes],
      event.startFrame,
      event.endFrame,
      keyframe,
      { ...event.instructions },
      event.subtitles?.clone() || undefined,
      event.createdAt,
      () => {
        this.sendEventsUpdated();
      },
    );
    this.executeOperation(op);
  }

  removeEditingDirectiveEvent(event: ContentControlledEditingDirective) {
    if (this._item.editingDirectives.findIndex((x) => x.id == event.id) > -1) {
      const startsAtMs = event.startsAtMs;
      const endsAtsMs = event.endsAtMs;
      const op = new ContentControlStudioRemoveEventOperation(
        event,
        this._item.editingDirectives,
        () => {
          this._selectedSegment = this.createNewSegment();
          this._selectedSegment.startsAtMs = startsAtMs;
          this._selectedSegment.endsAtMs = endsAtsMs;
          this.sendEventsUpdated();
        },
      );
      this.executeOperation(op);
    }
    return null;
  }

  addEventKeyframe(eventId: number, keyframe: ContentControlledKeyframe): void {
    const op = new ContentControlStudioAddKeyframeToEventOperation(
      eventId,
      this._item.editingDirectives,
      keyframe,
      () => {
        if (this.selectedSegment && eventId === this.selectedSegment.id) {
          const event = this._item.editingDirectives.find(
            (x) => x.id == eventId,
          );
          this.updateSelectedSegmentKeyframes([...event.keyframes]);
        }
        this.sendEventsUpdated();
      },
    );
    this.executeOperation(op);
  }
  updateSelectedEventKeyframe(keyframe: ContentControlledKeyframe): void {
    const eventId = this.selectedEvent.id;
    const currentKeyframe = this.selectedEvent.hasKeyframe(keyframe);
    if (currentKeyframe) {
      if (currentKeyframe.equal(keyframe) == false) {
        const op = new ContentControlStudioUpdateKeyframeToEventOperation(
          this.selectedEvent,
          currentKeyframe.clone(),
          keyframe.clone(),
          () => {
            const event = this._item.editingDirectives.find(
              (x) => x.id == eventId,
            );
            if (event) {
              this.updateSelectedSegmentKeyframes([...event.keyframes]);
            }
            this.sendEventsUpdated();
          },
        );
        this.debouncedExecuteOperation(op);
      }
    } else {
      this.addEventKeyframe(this.selectedEvent.id, keyframe);
    }

    this.updateSelectedSegmentKeyframes([...this.selectedEvent.keyframes]);
  }
  removeSelectedEventKeyframe(keyframe: ContentControlledKeyframe): void {
    const op = new ContentControlStudioRemoveKeyframeToEventOperation(
      this.selectedEvent,
      keyframe,
      () => {
        this.updateSelectedSegmentKeyframes([...this.selectedEvent.keyframes]);
        this.sendEventsUpdated();
      },
    );
    this.executeOperation(op);
    this.updateSelectedSegmentKeyframes([...this.selectedEvent.keyframes]);
  }

  setupAudio(ctx: AudioContext, gain: GainNode): void {
    this._audioCtx = ctx;
    this._audioGain = gain;
  }

  async searchItems(
    specification: ContentControlledItemsSpecifications,
  ): Promise<ContentControlItem[]> {
    return await this._repository.searchItems(specification);
  }
  async getItemByMediaId(mediaId: number): Promise<boolean> {
    const item = await this._ensureItemExists(mediaId);
    if (item) {
      this.setFPS(item.videoFps);
      const ts = await (
        this.tenantSettingsRepository as CachedTenantSettingsRepositoryImpl
      ).init();
      this._displayContentControlStudioTimeInFrames =
        ts.DisplayContentControlStudioTimeInFrames;
      this._mediaStartTimeStampMs = ts.UseMediaStartTimestampInScheduler
        ? item.mediaStartTimeStampMs
        : 0;
      if (item.reportableEvents.length > 0) {
        const oneFrame = +(1 / this.videoFps).toPrecision(12);
        const timestampsToFramesInMs = (time: number): number => {
          let fn = (time / 1000) * this.videoFps;
          let nextFrame = Math.floor(fn);
          if (+(fn - nextFrame).toPrecision(12) >= oneFrame) {
            nextFrame++;
          }
          return FormatNumber((nextFrame / this._videoFps) * 1000);
        };
        item.reportableEvents.forEach((re) => {
          if (item.editingDirectives.findIndex((x) => x.id == re.id) == -1) {
            const ed = re.toEventDirective(item.videoFps);
            ed.startsAtMs = timestampsToFramesInMs(ed.startsAtMs);
            ed.endsAtMs = timestampsToFramesInMs(ed.endsAtMs);
            if (ed.keyframes) {
              ed.keyframes.forEach((k) => {
                k.timeInMs = timestampsToFramesInMs(k.timeInMs);
              });
            }
            if (
              ts.ContentControlAIdetectionsSettings &&
              ts.ContentControlAIdetectionsSettings.length > 0
            ) {
              const aiDetectionSetting =
                ts.ContentControlAIdetectionsSettings.find(
                  (x) =>
                    x.isEnabled &&
                    x.requiredClass.class_name.toLocaleLowerCase() ==
                      ed.title.toLocaleLowerCase(),
                );
              if (
                aiDetectionSetting &&
                aiDetectionSetting.isAutomaticMitigation &&
                aiDetectionSetting.automaticMitigationAction !=
                  ContentControlledEventActionEnum.Empty
              ) {
                ed.action = aiDetectionSetting.automaticMitigationAction;
                ed.instructions = aiDetectionSetting.instructions;
                ed.isMitigated = true;
              }
            }
            item.editingDirectives.push(ed);
          }
        });
      }
      this._item = item;
      console.log(item);
      this.itemUpdated.next(!!this._item);
      return true;
    } else {
      return false;
    }
  }

  async save(): Promise<void> {
    const result = await this._repository.save(this._item);
    for (let contentControlStudioOperation of this._undoStack) {
      contentControlStudioOperation.isSaved = true;
    }
    this.itemSaved.next(result);
  }
  clear() {
    do this.undoOperation();
    while (this._undoStack.length != 0);
    this._redoStack = [];
    this.itemUpdated.next(true);
    this.sendEventsUpdated();
    this.selectedSegmentRemoved.next(-1);
  }

  setLiveHlsProviderForScreenshotConversion(
    provider: ITimestampToTimelineConverter | null,
  ): void {
    this._liveHlsProviderForScreenshotConversion = provider;
  }

  private async _ensureItemExists(
    mediaId: number,
  ): Promise<ContentControlItem> {
    let item = await this._repository.getItemByMediaId(mediaId, false);
    if (!item) {
      //upsert a new media content control item
      item = new ContentControlItem(
        this._commonUtils.generateGuid(),
        mediaId,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        false,
        false,
        null,
        null,
        ContentControlledVODSettings.empty(),
        null,
        [],
        [],
        false,
        null
      );
      if (await this._repository.save(item)) {
        item = await this._repository.getItemByMediaId(mediaId, false);
      }
    }
    return item;
  }
  private takeScreenshot(
    request?: ContentControlledEditingDirectiveScreenshot,
  ) {
    return new Observable((observer) => {
      if (!request) {
        observer.next(request);
        observer.complete();
        return;
      }

      // Screenshot from media by id (e.g. VideoClip thumbnails)
      if (request.mediaId != null) {
        Promise.all([
          this._mediaRepository.getMediaPreviewUrl(request.mediaId),
          this._mediaRepository.getMediaInfo(request.mediaId),
        ])
          .then(([url, mediaInfo]) => {
            if (mediaInfo.contentMediaType === MediaType.Image) {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                const offscreenCanvas = new OffscreenCanvas(82, 46);
                const offscreenCtx = offscreenCanvas.getContext('2d');
                if (offscreenCtx) {
                  offscreenCtx.drawImage(
                    img,
                    0,
                    0,
                    offscreenCanvas.width,
                    offscreenCanvas.height,
                  );
                  offscreenCanvas.convertToBlob().then((blob) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      request.screenshot = reader.result as string;
                      this.screenshotTaken.next(request);
                      observer.next(request);
                      observer.complete();
                    };
                    reader.readAsDataURL(blob);
                  });
                } else {
                  observer.complete();
                }
              };
              img.onerror = () => observer.complete();
              img.src = url;
            } else {
              const videoStage = document.createElement('video');
              videoStage.crossOrigin = 'anonymous';
              videoStage.src = url;
              videoStage.load();
              videoStage.addEventListener('loadeddata', () => {
                const onSeeked = () => {
                  videoStage.removeEventListener('seeked', onSeeked);
                  this.captureScreenshotFromVideo(videoStage, request, observer);
                };
                videoStage.addEventListener('seeked', onSeeked);
                videoStage.currentTime = request.at;
              });
              videoStage.addEventListener('error', () => observer.complete());
            }
          })
          .catch(() => observer.complete());
        return;
      }

      // When live, use existing HLS provider so the video element actually loads (plain <video> + HLS URL never fires loadeddata in most browsers)
      if (this._screenshotHLSProvider) {
        const videoStage = this._screenshotVideoElement;
        const onSeeked = () => {
          videoStage.removeEventListener("seeked", onSeeked);
          this.captureScreenshotFromVideo(videoStage, request, observer);
        };
        videoStage.addEventListener("seeked", onSeeked);
        this._screenshotHLSProvider.seekToVideoPlayerRelativeTimeInSecs(request.at);
        return;
      }

      const videoStage = document.createElement("video");
      videoStage.crossOrigin = "anonymous";
      videoStage.src = this._item.previewVideoUrl;
      videoStage.load();

      videoStage.addEventListener("loadeddata", () => {
        const capture = async () => {
          await this.captureScreenshotFromVideo(videoStage, request, observer);
        };

        videoStage.addEventListener("seeked", () => {
          capture();
        });
        videoStage.currentTime = request.at;
      });
    });
  }

  private async captureScreenshotFromVideo(
    videoStage: HTMLVideoElement,
    request: ContentControlledEditingDirectiveScreenshot,
    observer: { next: (v: unknown) => void; complete: () => void },
  ): Promise<void> {
    const offscreenCanvas = new OffscreenCanvas(82, 46);
    const offscreenCtx = offscreenCanvas.getContext("2d");

    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));

    offscreenCtx.drawImage(
      videoStage,
      0,
      0,
      offscreenCanvas.width,
      offscreenCanvas.height
    );

    let blob = await offscreenCanvas.convertToBlob();
    if (blob && blob.size < 1000) {
      await new Promise((r) => setTimeout(r, 100));
      offscreenCtx.drawImage(
        videoStage,
        0,
        0,
        offscreenCanvas.width,
        offscreenCanvas.height
      );
      blob = await offscreenCanvas.convertToBlob();
    }

    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });

    request.screenshot = base64;
    this.screenshotTaken.next(request);
    observer.complete();
  }

  startBeep(frequency: number = 440): void {
    /**
     * Starts a beep sound with the specified frequency.
     *
     * @param {number} frequency - The frequency of the beep sound (default is 440).
     */
    if (!this._audioOscillator) {
      this._audioOscillator = this._audioCtx.createOscillator();
      this._audioOscillator.type = "sine";
      this._audioOscillator.frequency.setValueAtTime(
        frequency,
        this._audioCtx.currentTime,
      );
      this._audioOscillator.connect(this._audioGain);
      this._audioOscillator.start();
    } else {
      if (this._audioOscillator.frequency.value != frequency) {
        this._audioOscillator.frequency.setValueAtTime(
          frequency,
          this._audioCtx.currentTime,
        );
      }
    }
  }

  stopBeep(): void {
    if (this._audioOscillator) {
      this._audioOscillator.stop();
      this._audioOscillator.disconnect();
      this._audioOscillator = null;
    }
  }
  undoOperation(): void {
    if (this._undoStack.length === 0) return;
    const operation = this._undoStack.pop();
    operation.undo();
    this._redoStack.push(operation);
  }
  redoOperation(): void {
    if (this._redoStack.length === 0) return;
    const operation = this._redoStack.pop();
    operation.do();
    this._undoStack.push(operation);
  }

  private executeOperation(operation: ContentControlStudioOperation) {
    operation.do();
    this._undoStack.push(operation);
    this._redoStack = []; // Clear redo stack on new operation
  }
  private debouncedExecuteOperation(operation: ContentControlStudioOperation) {
    this._debounceOperationSubject.next(operation);
  }
  private sendEventsUpdated() {
    this.debounce(() => {
      this.eventsUpdated.next();
      if (this._isLive) this._liveSaveSubject.next();
    }, 250);
  }
  private debounce(callback: () => void, delay: number) {
    clearTimeout(this._eventsUpdatedDebounceTimeout);
    this._eventsUpdatedDebounceTimeout = setTimeout(callback, delay);
  }
  private async updateSegmentScreenshots(
    segment: ContentControlledEditingDirectiveViewModel,
  ): Promise<void> {
    const segmentId = segment.id;
    if (
      segment.mediaId != null &&
      segment.action === ContentControlledEventActionEnum.VideoClip
    ) {
      const durationSec = (segment.endsAtMs - segment.startsAtMs) / 1000;
      this.screenshotQueue.next(
        new ContentControlledEditingDirectiveScreenshot(
          segmentId,
          'in',
          0,
          undefined,
          undefined,
          segment.mediaId,
        ),
      );
      this.screenshotQueue.next(
        new ContentControlledEditingDirectiveScreenshot(
          segmentId,
          'out',
          durationSec,
          undefined,
          undefined,
          segment.mediaId,
        ),
      );
      return;
    }
    let startsAtSecs: number;
    let endsAtSecs: number;
    if (this.isLive && (this._liveHlsProviderForScreenshotConversion ?? this._screenshotHLSProvider)) {
      // Live: segment times are timestamps (ms). Use playback provider (or screenshot provider) to convert to
      // playlist-relative seconds so seek targets match the segment.
      const converter = this._liveHlsProviderForScreenshotConversion ?? this._screenshotHLSProvider;
      startsAtSecs = converter.timestampUtcToPlayerRelativeTimeInSeconds(segment.startsAtMs);
      endsAtSecs = converter.timestampUtcToPlayerRelativeTimeInSeconds(segment.endsAtMs);
    } else {
      startsAtSecs = segment.startsAtMs / 1000;
      endsAtSecs = segment.endsAtMs / 1000;
    }
    this.screenshotQueue.next(new ContentControlledEditingDirectiveScreenshot(segmentId, "in", startsAtSecs));
    this.screenshotQueue.next(new ContentControlledEditingDirectiveScreenshot(segmentId, "out", endsAtSecs));
  }
  private setFPS(fps: number) {
    if (fps < 0) {
      // panic
    }
    this._vidoeFpsIsDF = true;
    this._videoFps = fps;
    //const decimalPart = fps - Math.floor(fps);
    //if (decimalPart >= 0.9) {
    this._vidoeFpsIsDF = true;
    this._videoFps = fps;
    // } else {
    //   this._vidoeFpsIsDF = false;
    //   this._videoFps = Math.floor(fps);
    // }
  }
  async renderItems(items: Array<ContentControlItem>): Promise<{
    success: string[];
    errors: string[];
  }> {
    const result: string[] = [];
    const errors: string[] = [];
    const defaultBucket = this._tenantTranscodingDestinations.find(
      (d) => d.defaultSelected,
    );
    if (items.length == 0 && this._item) {
      items = [this._item];
    }
    if (defaultBucket && items && items.length > 0) {
      for (let item of items) {
        try {
          const mediaItem = await this._mediaRepository.getMediaInfo(
            item.mediaId,
          );
          const tenantSettings =
            await this.tenantSettingsRepository.getTenantSettings();
          const transcoderJobDetails =
            TranscoderJobDetails.createNewContentControlItemJob(
              this._commonUtils,
              `Content Control Item ${item.mediaName} Job`,
              tenantSettings.ContentControlTranscodingDestinationId,
              tenantSettings.ContentControlTranscodingProfileId,
              item.uid,
              mediaItem.contentDuration.asMilliseconds(),
            );
          await this._mediaTranscoderRepository.createNewJob(
            transcoderJobDetails,
          );
          item.mediaTranscoderJobUid = transcoderJobDetails.id;
          await this._contentControlRepository.associateTranscodingJob(
            item.uid,
            transcoderJobDetails.id,
          );
          result.push(
            `Transcoder job for the item ${item.mediaName} scheduled!`,
          );
        } catch (e) {
          errors.push(
            ` failed to schedule transcoding job for ${item.mediaName}`,
          );
          console.error(e);
        }
      }
    }
    return {
      success: result,
      errors: errors,
    };
  }
  async downloadRenderedVideo(
    items: Array<ContentControlItem>,
  ): Promise<{ success: { url: string; name: string }[]; errors: string[] }> {
    const result: { url: string; name: string }[] = [];
    const errors: string[] = [];
    const defaultBucket = this._tenantTranscodingDestinations.find(
      (d) => d.defaultSelected,
    );
    if (items.length == 0 && this._item) {
      items = [this._item];
    }
    if (defaultBucket && items && items.length > 0) {
      for (let item of items) {
        if (!item.mediaTranscoderJobUid) {
          errors.push(`Item ${item.mediaName} has no transcoder job uid`);
          continue;
        }
        try {
          const transcodingJobDetails =
            await this._mediaTranscoderRepository.getTranscodingJobDetails(
              item.mediaTranscoderJobUid,
            );
          if (transcodingJobDetails.status == ETranscodingJobStatus.History) {
            transcodingJobDetails.transcodingItems.map((x) => {
              if (
                x.status ==
                  EMediaTranscodingItemStatus.DeliveredToDestinations ||
                x.status == EMediaTranscodingItemStatus.Finished
              ) {
                result.push({
                  url: x.resultUrl,
                  name: `${item.mediaName}-${item.uid}`,
                });
              } else {
                errors.push(
                  `Item ${item.mediaName} hasn't finished transcoding yet`,
                );
              }
            });
          }
        } catch (e) {
          errors.push(`Item ${item.mediaName} has no transcoder job uid`);
        }
      }
      return {
        success: result,
        errors: errors,
      };
    }
  }
  private async postItemLoadActions(item: ContentControlItem) {
    // Live uses a shared HLS video for screenshots; only one seek/capture at a time is valid.
    // Unsubscribe the constructor's high-concurrency pipeline so the serial pipeline below handles all requests.
    if (this.isLive) {
      this._screenshotQueueSubscription?.unsubscribe();
      this._screenshotQueueSubscription = null;
    }
    this.screenshotQueue
      .pipe(
        bufferTime(100, undefined, this.isLive ? 1 : 2), // Only process 1 at a time for live
        concatMap((batch) =>
          from(batch).pipe(
            mergeMap(
              (request) =>
                this.takeScreenshot(request).pipe(
                  timeout({
                    first: 10000,
                    each: 5000,
                    with: () =>
                      throwError(
                        () => new Error("Screenshot timeout exceeded"),
                      ),
                  }),
                ),
              this.isLive ? 1 : 2, // Only allow 1 concurrent for live
            ),
            toArray(),
            catchError((error) => {
              console.error("Screenshot error:", error);
              return of([]); // Return empty array to continue processing
            }),
          ),
        ),
      )
      .subscribe({
        error: (err) => console.error("Processed screenshots Error:", err),
      });
    this.setFPS(item.videoFps);

    const ts = await (
      this.tenantSettingsRepository as CachedTenantSettingsRepositoryImpl
    ).init();
    this._tenantSettings = ts;
    this._displayContentControlStudioTimeInFrames =
      ts.DisplayContentControlStudioTimeInFrames;
    this._mediaStartTimeStampMs = ts.UseMediaStartTimestampInScheduler
      ? item.mediaStartTimeStampMs
      : 0;
    if (item.reportableEvents.length > 0) {
      const oneFrame = +(1 / this.videoFps).toPrecision(12);
      const timestampsToFramesInMs = (time: number): number => {
        let fn = (time / 1000) * this.videoFps;
        let nextFrame = Math.floor(fn);
        if (+(fn - nextFrame).toPrecision(12) >= oneFrame) {
          nextFrame++;
        }
        return FormatNumber((nextFrame / this._videoFps) * 1000);
      };
      item.reportableEvents.forEach((re) => {
        if (item.editingDirectives.findIndex((x) => x.id == re.id) == -1) {
          const ed = re.toEventDirective(item.videoFps);
          ed.startsAtMs = timestampsToFramesInMs(ed.startsAtMs);
          ed.endsAtMs = timestampsToFramesInMs(ed.endsAtMs);
          if (ed.keyframes) {
            ed.keyframes.forEach((k) => {
              k.timeInMs = timestampsToFramesInMs(k.timeInMs);
            });
          }
          if (
            ts.ContentControlAIdetectionsSettings &&
            ts.ContentControlAIdetectionsSettings.length > 0
          ) {
            const aiDetection = ts.ContentControlAIdetectionsSettings.find(
              (x) =>
                x.isEnabled &&
                x.requiredClass.class_name.toLocaleLowerCase() ==
                  ed.title.toLocaleLowerCase(),
            );
            if (
              aiDetection &&
              aiDetection.automaticMitigationAction !=
                ContentControlledEventActionEnum.Empty
            ) {
              ed.action = aiDetection.automaticMitigationAction;
              ed.instructions = aiDetection.instructions;
              ed.isMitigated = true;
            }
          }
          item.editingDirectives.push(ed);
        }
      });
    }
    this._item = item;
    if (this.isLive) {
      this.createScreenshotHLSProvider(this.createShadowVideoElement());
    }
    console.log(item);
    this.itemUpdated.next(!!this._item);
  }
  private createShadowVideoElement() {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    return video;
  }
  private createScreenshotHLSProvider(videoStage: HTMLVideoElement) {
    this._screenshotVideoElement = videoStage;
    this._screenshotHLSProvider = HlsStreamVideoSource.create({
      videoElement: videoStage,
      hlsStreamUrl: this._item.previewVideoUrl,
      fps: this._item.videoFps,
    });
    this._screenshotHLSProvider.seekToVideoPlayerRelativeTimeInSecs(1);
    return this._screenshotHLSProvider;
  }
  private cleanupScreenshotResources() {
    if (this._screenshotHLSProvider) {
      this._screenshotHLSProvider.destroy();
      this._screenshotHLSProvider = null;
    }
  }
}
